import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { usePosStore } from '../store/usePosStore';
import { formatPosMoney } from './posMoneyFormat';
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import { usePosCheckoutStore } from '../store/usePosCheckoutStore';
import { showInfo, showSuccess } from '../../shared/ui/appToast';
import type { PosCartPersistenceScope, PosCartPromoSnapshot } from '../domain/posParkedSaleTypes';
import { canParkCart, snapshotCartSummary } from './posCartPersistence';
import {
  discardSaleDraft,
  getActiveSaleDraft,
  saveSaleDraft,
  type RemoteSaleDraft,
} from '../infra/posSaleDraftsRepo';

export type UsePosRemoteSaleDraftParams = {
  scope: PosCartPersistenceScope | null;
  canEdit: boolean;
  promo: PosCartPromoSnapshot;
  onRestorePromo: (promo: PosCartPromoSnapshot) => void;
  onCartSnapshotRestored?: () => void;
};

function formatCartLoadSummary(lineCount: number, total: number): string {
  const itemsLabel = lineCount === 1 ? '1 ítem' : `${lineCount} ítems`;
  return `${itemsLabel} · ${formatPosMoney(total)}`;
}

export function usePosRemoteSaleDraft({
  scope,
  canEdit,
  promo,
  onRestorePromo,
  onCartSnapshotRestored,
}: UsePosRemoteSaleDraftParams) {
  const { confirm, ConfirmModal } = useOperationalConfirm();
  const cart = usePosStore((s) => s.cart);
  const replaceCart = usePosStore((s) => s.replaceCart);
  const clearCart = usePosStore((s) => s.clearCart);
  const checkoutVisible = usePosCheckoutStore((s) => s.checkoutVisible);
  const [draft, setDraft] = useState<RemoteSaleDraft | null>(null);
  const [loading, setLoading] = useState(false);
  const parkingRef = useRef(false);
  const resumingRef = useRef(false);
  const scopeKey = useMemo(
    () => (scope ? `${scope.organizationId}|${scope.branchId}|${scope.userId}` : null),
    [scope],
  );

  const refreshDraft = useCallback(async () => {
    if (!scope || !canEdit) {
      setDraft(null);
      return;
    }
    setLoading(true);
    try {
      setDraft(await getActiveSaleDraft(scope));
    } catch (error) {
      presentUnexpectedFailure({
        module: 'pos',
        op: 'sale-draft.load',
        error,
        title: 'Venta pausada',
        userMessage: 'No se pudo consultar la venta pausada.',
      });
    } finally {
      setLoading(false);
    }
  }, [canEdit, scope]);

  useEffect(() => {
    void refreshDraft();
  }, [refreshDraft, scopeKey]);

  const applySnapshotToCart = useCallback(
    (remoteDraft: RemoteSaleDraft) => {
      replaceCart(remoteDraft.snapshot.cart);
      onRestorePromo(
        remoteDraft.snapshot.promo ?? {
          appliedPromoCode: null,
          selectedCashierPromotionId: null,
        },
      );
      onCartSnapshotRestored?.();
    },
    [onCartSnapshotRestored, onRestorePromo, replaceCart],
  );

  const parkCurrentSale = useCallback(async () => {
    if (!scope || !canEdit || parkingRef.current) return;
    if (checkoutVisible) {
      showInfo('Cobro abierto', 'Cerrá el cobro antes de pausar la venta.');
      return;
    }
    if (!canParkCart(cart)) {
      showInfo('Carrito vacío', 'Agregá productos antes de pausar la venta.');
      return;
    }
    parkingRef.current = true;
    try {
      const replaceExisting = Boolean(draft);
      if (replaceExisting) {
        const ok = await confirm({
          title: 'Reemplazar venta pausada',
          message: 'Ya tenés una venta pausada. Si seguís, se reemplaza por la de ahora.',
          confirmLabel: 'Reemplazar',
          destructive: true,
        });
        if (!ok) return;
      }
      const saved = await saveSaleDraft({
        scope,
        cart,
        promo,
        replaceExisting,
      });
      setDraft(saved);
      clearCart();
      onRestorePromo({ appliedPromoCode: null, selectedCashierPromotionId: null });
      const summary = snapshotCartSummary(saved.snapshot);
      showSuccess('Venta pausada', formatCartLoadSummary(summary.lineCount, summary.total));
    } catch (error) {
      presentUnexpectedFailure({
        module: 'pos',
        op: 'sale-draft.save',
        error,
        title: 'Pausar venta',
        userMessage: 'No se pudo pausar la venta. El carrito sigue abierto.',
      });
    } finally {
      parkingRef.current = false;
    }
  }, [canEdit, cart, checkoutVisible, clearCart, confirm, draft, onRestorePromo, promo, scope]);

  const resumeParkedSale = useCallback(async () => {
    if (!scope || !canEdit || resumingRef.current) return;
    resumingRef.current = true;
    try {
      const current = await getActiveSaleDraft(scope);
      if (!current) {
        setDraft(null);
        showInfo('Venta pausada', 'No encontramos una venta pausada vigente.');
        return;
      }
      if (cart.length > 0) {
        const ok = await confirm({
          title: 'Reemplazar carrito actual',
          message: 'Se reemplaza el carrito de ahora por la venta pausada.',
          confirmLabel: 'Continuar',
          destructive: true,
        });
        if (!ok) return;
      }
      setDraft(current);
      applySnapshotToCart(current);
      const summary = snapshotCartSummary(current.snapshot);
      showSuccess('Venta cargada', formatCartLoadSummary(summary.lineCount, summary.total));
    } catch (error) {
      presentUnexpectedFailure({
        module: 'pos',
        op: 'sale-draft.resume',
        error,
        title: 'Cargar venta',
        userMessage: 'No se pudo cargar la venta pausada.',
      });
    } finally {
      resumingRef.current = false;
    }
  }, [applySnapshotToCart, canEdit, cart.length, confirm, scope]);

  const discardParkedSale = useCallback(async () => {
    if (!scope || !canEdit || !draft) return;
    try {
      await discardSaleDraft({ scope, draftId: draft.id });
      setDraft(null);
      showSuccess('Venta descartada', 'Se eliminó la venta pausada.');
    } catch (error) {
      presentUnexpectedFailure({
        module: 'pos',
        op: 'sale-draft.discard',
        error,
        title: 'Descartar venta',
        userMessage: 'No se pudo descartar la venta pausada.',
      });
    }
  }, [canEdit, draft, scope]);

  const clearPersistenceAfterSale = useCallback(async () => {
    if (!scope || !draft) return;
    // Transitional cleanup until process_sale_command consumes sale_draft_id atomically.
    try {
      await discardSaleDraft({ scope, draftId: draft.id });
      setDraft(null);
    } catch (error) {
      presentUnexpectedFailure({
        module: 'pos',
        op: 'sale-draft.consume-transition',
        error,
        title: 'Venta registrada',
        userMessage: 'La venta se registró, pero no pudimos limpiar la pausa remota.',
      });
    }
  }, [draft, scope]);

  return {
    hasParkedSale: Boolean(draft),
    parkedSummary: draft ? snapshotCartSummary(draft.snapshot) : null,
    activeDraftId: draft?.id ?? null,
    draftLoading: loading,
    canParkCurrentSale: canEdit && canParkCart(cart) && !checkoutVisible,
    parkCurrentSale,
    resumeParkedSale,
    discardParkedSale,
    clearPersistenceAfterSale,
    ConfirmModal,
  };
}
